import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { getNoteVersions } from '../../api/sharedNotes';
import { createMarkdownComponents } from '../common/MarkdownComponents';

const SharedNoteVersions = ({ open, onClose, noteId, noteTitle }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);

  useEffect(() => {
    if (open && noteId) {
      loadVersions();
    }
  }, [open, noteId]);

  const loadVersions = async () => {
    try {
      setLoading(true);
      const data = await getNoteVersions(noteId);
      setVersions(data.items);
    } catch (err) {
      console.error('Error loading versions:', err);
      setError('Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleVersionClick = (version) => {
    setSelectedVersion(selectedVersion?.id === version.id ? null : version);
  };

  const isDarkMode = document.documentElement.classList.contains('dark');

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div 
        className="absolute inset-0 bg-black opacity-50"
        onClick={onClose}
      ></div>
      <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-lg z-10 max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">
              Version History - {noteTitle}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:text-ui-light-text-secondary dark:hover:text-ui-dark-text-secondary"
            >
              <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-200 rounded-md border border-red-200 dark:border-red-800">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <svg className="w-8 h-8 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
          ) : versions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-ui-light-text-secondary dark:text-ui-dark-text-secondary">No version history available</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              {versions.map((version) => (
                <div key={version.id} className="border border-ui-light-border dark:border-ui-dark-border rounded-lg">
                  <div 
                    className="p-4 cursor-pointer hover:bg-ui-light-background dark:hover:bg-ui-dark-background transition-colors"
                    onClick={() => handleVersionClick(version)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-200">
                          v{version.version_number}
                        </span>
                        <span className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                          {format(new Date(version.created_at), 'MMM d, yyyy h:mm a')}
                        </span>
                        <span className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                          by {version.created_by.full_name}
                        </span>
                      </div>
                      <svg 
                        className={`w-5 h-5 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary transition-transform ${
                          selectedVersion?.id === version.id ? 'rotate-180' : ''
                        }`} 
                        xmlns="http://www.w3.org/2000/svg" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    {version.title && (
                      <div className="mt-2">
                        <h4 className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
                          {version.title}
                        </h4>
                      </div>
                    )}
                  </div>
                  
                  {selectedVersion?.id === version.id && (
                    <div className="border-t border-ui-light-border dark:border-ui-dark-border p-4 bg-ui-light-background dark:bg-ui-dark-background">
                      <div className="prose dark:prose-invert max-w-none">
                        <ReactMarkdown
                          components={createMarkdownComponents(isDarkMode)}
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                        >
                          {version.content}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharedNoteVersions;